import * as Sentry from '@sentry/react';
import { useToast } from '@televet/kibble-ui';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useTracking } from 'shared/providers/TrackingProvider';
import React, { useState } from 'react';
import { useAvatarGroupSelectOptions } from 'shared/components/Avatars/AvatarGroupSelect';
import useClinicUser from 'shared/hooks/useClinicUser';
import { ChannelVisibility, useCreateTeamChannelMutation } from 'shared/types/graphql';
import ParticipantsInput from './ParticipantsInput';
import { TeamChatEvents } from 'shared/providers/TrackingProvider/EventNames';

interface ICreateDirectMessageModalModalProps {
  onSuccess: (newChannelId: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

const CreateDirectMessageModal = ({ onSuccess, isOpen, onClose }: ICreateDirectMessageModalModalProps): JSX.Element => {
  const { clinicUser } = useClinicUser();
  const { trackEvent } = useTracking();
  const [assigneesValidationMessage, setAssigneesValidationMessage] = useState('');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const toast = useToast();
  const groupSelectOptions = useAvatarGroupSelectOptions();

  const [createDirectMessage, { loading: isCreatingDirectMessage }] = useCreateTeamChannelMutation();

  const buildFriendlyName = (): string => {
    let friendlyName = `${clinicUser?.firstName} ${clinicUser?.lastName}, `;
    friendlyName += groupSelectOptions
      .filter((option) => assigneeIds.includes(option.value.id))
      .map((option) => option.text)
      .join(', ');

    return friendlyName;
  };

  const handleCreateClick = async (): Promise<void> => {
    if (assigneeIds.length === 0) {
      setAssigneesValidationMessage('Please select at least one team member');
      return;
    }

    try {
      const result = await createDirectMessage({
        variables: {
          data: {
            friendlyName: buildFriendlyName(),
            assigneeIds: [...assigneeIds, clinicUser?.id || ''],
            channelVisibility: ChannelVisibility.DirectMessage,
          },
        },
      });
      const newChannelId = result?.data?.createTeamChannel2?.id;
      if (!newChannelId) {
        throw new Error('Error creating direct message');
      }
      trackEvent(TeamChatEvents.DirectMessageChannelCreated);
      onSuccess(newChannelId);
    } catch (e) {
      Sentry.captureException(e);
      toast({
        status: 'error',
        description: 'Whoops! An error occurred while creating the direct message. Please try again.',
        duration: 5000,
      });
      console.error(e);
    } finally {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader pb={1}>
        <Heading size="sm" mb={4}>
          Create Direct Message
        </Heading>
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody>
        <ParticipantsInput
          selectedUserIds={assigneeIds}
          onChange={setAssigneeIds}
          required={true}
          showAddAllTeamMembers={false}
          showTooltip={false}
          customDescription="Select which team member(s) you want to chat with."
          autofocus={true}
        />
        {assigneesValidationMessage && (
          <Text as="p" mt={2} mb={0} color="text.danger" size="xs">
            {assigneesValidationMessage}
          </Text>
        )}
        <ModalFooterButtons
          px={0}
          submitText={'Start Direct Message'}
          onSubmit={handleCreateClick}
          onCancel={onClose}
          isLoading={isCreatingDirectMessage}
        />
      </ModalBody>
    </Modal>
  );
};

export default CreateDirectMessageModal;
